import { ApiError, apiClient } from '@/shared/api/http-client';

import type {
    AddLicenseInput,
    GetPracticeMemberInsightsOutput,
    GetPracticeMemberPreferencesOutput,
    License,
    OnboardingStep,
    PracticeMember,
    PracticeMemberUpdateWelcomeGuideInput,
    UpdateInsightsInput,
    UpdateLicenseInput,
    UpdatePracticeMemberInput,
} from '../model/practice-member.types';

export const practiceMemberApi = {
    get: async (signal?: AbortSignal): Promise<PracticeMember> => {
        return apiClient.get('/practice-members/me', signal);
    },
    update: async (
        data: UpdatePracticeMemberInput,
        signal?: AbortSignal,
    ): Promise<{ id: string; updatedAt: string }> => {
        return apiClient.patch('/practice-members/me', data, signal);
    },
    updateOnboardingStep: async (
        onboardingStep: OnboardingStep,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.patch('/practice-members/me/onboarding', { onboardingStep }, signal);
    },
    deleteOnboardingStep: async (signal?: AbortSignal): Promise<void> => {
        return apiClient.delete('/practice-members/me/onboarding', signal);
    },
    getPreferences: async (
        signal?: AbortSignal,
    ): Promise<GetPracticeMemberPreferencesOutput | null> => {
        try {
            return await apiClient.get<GetPracticeMemberPreferencesOutput>(
                '/practice-members/me/preferences',
                signal,
            );
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) {
                return null;
            }
            throw error;
        }
    },
    updateWelcomeGuide: async (
        data: PracticeMemberUpdateWelcomeGuideInput,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.patch<void>('/practice-members/me/welcome-guide', data, signal);
    },

    getInsights: async (signal?: AbortSignal): Promise<GetPracticeMemberInsightsOutput> => {
        return apiClient.get<GetPracticeMemberInsightsOutput>(
            '/practice-members/me/insights',
            signal,
        );
    },
    updateInsights: async (payload: UpdateInsightsInput, signal?: AbortSignal): Promise<void> => {
        return apiClient.patch<void>('/practice-members/me/insights', { payload }, signal);
    },
    dismissGuidance: async (key: string, signal?: AbortSignal): Promise<void> => {
        return apiClient.post<void>('/practice-members/me/guidance/dismiss', { key }, signal);
    },

    getLicenses: async (signal?: AbortSignal): Promise<License[]> => {
        return apiClient.get<License[]>('/practice-members/me/licenses', signal);
    },
    addLicense: async (data: AddLicenseInput, signal?: AbortSignal): Promise<{ id: string }> => {
        return apiClient.post('/practice-members/me/licenses', data, signal);
    },
    updateLicense: async (
        licenseId: string,
        data: UpdateLicenseInput,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.patch(`/practice-members/me/licenses/${licenseId}`, data, signal);
    },
    // 204 with no body — the caller refetches `getLicenses` rather than reading a response.
    removeLicense: async (licenseId: string, signal?: AbortSignal): Promise<void> => {
        return apiClient.delete(`/practice-members/me/licenses/${licenseId}`, signal);
    },
};
